import { observable } from 'mobx';
import { delay, noop } from '@src/utils/common-util';
import BaseModalStore from '@src/components/modal-container/base-modal-store';
import { message } from 'antd';
import TinyInputStore from '@src/components/tiny-input/store';
import SmsCodeInputStore from '@src/business-components/sms-code-input/store-version/store';
import { useUnmount } from 'ahooks';
import $$tracker from '@src/tracker';

const getErrorMessage = error => {
  const response = error && error.serviceResponse;
  return (response && (response.message || response.msg)) || (error && (error.message || error.msg)) || '操作失败，请稍后重试';
};

class VerificationSmsCodeInputStore extends SmsCodeInputStore {
  countdownToken = 0;

  // 接口按登录态识别绑定手机，页面只有脱敏号码；极验仍由父类发送流程处理。
  validate() {}

  countDown(countDown) {
    const token = ++this.countdownToken;
    return this.continueCountdown(countDown, token);
  }

  async continueCountdown(countDown, token) {
    this.smsFirstTimes = true;
    const nextCountDown = countDown - 1;
    if (nextCountDown < 1) {
      this.countDownNumber = null;
      return;
    }
    this.countDownNumber = nextCountDown;
    await delay(1000);
    if (token === this.countdownToken) {
      await this.continueCountdown(nextCountDown, token);
    }
  }

  cancelCountdown() {
    this.countdownToken += 1;
    this.countDownNumber = null;
    this.smsFirstTimes = false;
    this.isLoading = false;
  }

  use() {
    useUnmount(() => this.cancelCountdown());
  }
}

class VerificationModalStore extends BaseModalStore {
  @observable phone = '';
  @observable phoneDisplay = '';
  @observable isSubmitting = false;

  idNumberInputStore = new TinyInputStore();
  codeInputStore = new TinyInputStore();
  smsCodeInputStore;

  onSuccess = noop;
  closeCallback = noop;
  showError = errorMessage => message.error(errorMessage);

  constructor(options = {}) {
    super(options);
    Object.assign(this, options);
    this.smsCodeInputStore = new VerificationSmsCodeInputStore({
      sendCodeRequest: this.sendCodeRequest
    });
  }

  get idNumber() {
    return this.idNumberInputStore.trimmedValue;
  }

  set idNumber(value) {
    this.idNumberInputStore.onChange(value);
  }

  get code() {
    return this.codeInputStore.trimmedValue;
  }

  set code(value) {
    this.codeInputStore.onChange(value);
  }

  get isSendingCode() {
    return this.smsCodeInputStore.isLoading;
  }

  get countdown() {
    return this.smsCodeInputStore.countDownNumber === null ? 0 : this.smsCodeInputStore.countDownNumber + 1;
  }

  setPhone(phone) {
    const value = String(phone || '');
    this.phone = value;
    this.phoneDisplay = /^(\d{3})\d{4}(\d{4})$/.test(value) ? value.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2') : value;
    this.smsCodeInputStore.phone = value;
  }

  setIdNumber(value) {
    this.idNumberInputStore.onChange(String(value || '').trim());
  }

  setCode(value) {
    this.codeInputStore.onChange(String(value || '').trim());
  }

  resetCountdown() {
    this.smsCodeInputStore.cancelCountdown();
  }

  sendCodeRequest = ({ phone, ...captchaParams }) => this.$ztService.getPhoneCode(captchaParams);

  // 保留弹窗 Store 的调用入口，状态与倒计时统一归属 SmsCodeInputStore。
  sendCode() {
    if (this.smsCodeInputStore.isLoading || this.smsCodeInputStore.countDownNumber !== null) {
      return Promise.resolve(false);
    }
    return this.requestCode();
  }

  requestCode() {
    this.smsCodeInputStore.phone = this.phone;
    return this.smsCodeInputStore.onSmsButtonClick();
  }

  async submitReservation() {
    $$tracker.clickIdentityValidate();
    if (this.isSubmitting) {
      return false;
    }
    if (!this.idNumber || !this.code) {
      this.showError(!this.idNumber ? '请输入身份证号码' : '请输入验证码');
      return false;
    }
    this.isSubmitting = true;
    try {
      const result = await this.$ztService.activate(this.code, this.idNumber);
      this.onClickClose();
      await this.onSuccess(result);
      return true;
    } catch (error) {
      this.showError(getErrorMessage(error));
      return false;
    } finally {
      this.isSubmitting = false;
    }
  }

  onClickClose = () => {
    this.isOpen = false;
    this.isActive = false;
    this.resetCountdown();
    this.closeCallback();
    this.clearStyle();
  };

  use() {
    super.use();
    this.smsCodeInputStore.use();
  }
}

export default VerificationModalStore;
